/**
 * Subscription API (RTK Query)
 * Optimized subscription state management using createApi
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { apiClient } from '@/auth';
import { apiClientBaseQuery } from '@/store/api/apiClientBaseQuery';
import type {
    SubscriptionPlan,
    CurrentSubscription,
    SubscriptionUsage,
    CheckoutRequest,
    CheckoutResponse,
    TransactionsResponse,
} from '../types/subscription.types';

// ─── API ───────────────────────────────────────────────────────────────────────

export const subscriptionApi = createApi({
    reducerPath: 'subscriptionApi',
    baseQuery: apiClientBaseQuery,
    tagTypes: ['CurrentSubscription', 'SubscriptionUsage'],
    endpoints: (builder) => ({
        getPlans: builder.query<SubscriptionPlan[], boolean>({
            query: (purchasableOnly = false) => ({
                url: 'subscriptions/plans',
                params: { purchasable_only: purchasableOnly },
            }),
            transformResponse: (res: { plans: SubscriptionPlan[] }) => res.plans,
        }),

        getCurrentSubscription: builder.query<CurrentSubscription | null, void>({
            queryFn: async () => {
                try {
                    const response = await apiClient.get('subscriptions/current');
                    return { data: response.data as CurrentSubscription };
                } catch (error: unknown) {
                    const apiError = error as { response?: { status?: number; data?: unknown }; message?: string };
                    if (apiError.response?.status === 404) {
                        return { data: null };
                    }
                    return { error: { status: apiError.response?.status ?? 500, data: apiError.response?.data ?? apiError.message } };
                }
            },
            providesTags: ['CurrentSubscription'],
        }),

        getSubscriptionUsage: builder.query<SubscriptionUsage | null, void>({
            queryFn: async () => {
                try {
                    const response = await apiClient.get('subscriptions/usage');
                    return { data: response.data as SubscriptionUsage };
                } catch (error: unknown) {
                    const apiError = error as { response?: { status?: number; data?: unknown }; message?: string };
                    if (apiError.response?.status === 404) {
                        return { data: null };
                    }
                    return { error: { status: apiError.response?.status ?? 500, data: apiError.response?.data ?? apiError.message } };
                }
            },
            providesTags: ['SubscriptionUsage'],
        }),

        // Paginated transactions
        getTransactions: builder.query<TransactionsResponse, { page?: number; pageSize?: number }>({
            query: ({ page = 1, pageSize = 10 }) => ({
                url: 'subscriptions/transactions',
                params: { page, page_size: pageSize },
            }),
        }),

        createCheckout: builder.mutation<CheckoutResponse, CheckoutRequest>({
            query: (checkoutData) => ({
                url: 'subscriptions/checkout',
                method: 'POST',
                body: checkoutData,
            }),
            invalidatesTags: ['CurrentSubscription', 'SubscriptionUsage'],
        }),

        cancelSubscription: builder.mutation<string, void>({
            query: () => ({ url: 'subscriptions/cancel', method: 'POST' }),
            invalidatesTags: ['CurrentSubscription', 'SubscriptionUsage'],
        }),

        reactivateSubscription: builder.mutation<string, void>({
            query: () => ({ url: 'subscriptions/reactivate', method: 'POST' }),
            invalidatesTags: ['CurrentSubscription', 'SubscriptionUsage'],
        }),
    }),
});

export const {
    useGetPlansQuery,
    useLazyGetPlansQuery,
    useGetCurrentSubscriptionQuery,
    useGetSubscriptionUsageQuery,
    useGetTransactionsQuery,
    useLazyGetTransactionsQuery,
    useCreateCheckoutMutation,
    useCancelSubscriptionMutation,
    useReactivateSubscriptionMutation,
} = subscriptionApi;

// Export reducer
export default subscriptionApi.reducer;
